import { inject, Injectable } from '@angular/core';
import { AuthGuard } from '@services/guards/auth-guard';
import { PharmaciesSearchStore } from '@services/pharmacies.search.store';
import { SettingsStore } from '@services/settings.store';
import { InventorySettings } from '@shared/models/settings/invetory-settings';
import { lastValueFrom } from 'rxjs';

export const INVENTORY_TENANTS = ['INCY'];

@Injectable({ providedIn: 'root' })
export class AuthInventoryGuard extends AuthGuard {
  private readonly pharmaciesStore = inject(PharmaciesSearchStore);
  private readonly settingsStore = inject(SettingsStore);

  async isAccessGranted(routeUrl: string, featureFlagKey: string, params: Record<string, string>) {
    const access = await super.isAccessGranted(routeUrl, featureFlagKey, params);
    const tenantIds = this.getTenantIds(routeUrl, params);
    const granted = access && (await this.isInventoryAvailableForAny(params.ncpdp, tenantIds));
    if (!granted) console.warn('AuthInventoryGuard', { ncpdp: params.ncpdp, tenantIds });
    return granted;
  }

  async isInventoryAvailableForAny(ncpdp: string, tenantIds: string[]) {
    const checks = await Promise.all(tenantIds.map(tenantId => this.isInventoryAvailable(ncpdp, tenantId)));
    return checks.some(Boolean);
  }

  async isInventoryAvailable(ncpdp: string, tenantId: string) {
    if (!ncpdp) {
      console.error('AuthInventoryGuard: Param ncpdp is required', ncpdp);
      return false;
    }

    if (!tenantId) {
      console.error('AuthInventoryGuard: Param tenantId is required', tenantId);
      return false;
    }

    return await this.isAvailableForTenant(ncpdp, tenantId);
  }

  async isAvailableForTenant(ncpdp: string, tenantId: string) {
    if (!this.userService.hasAccessToTenant(tenantId)) {
      console.warn('AuthInventoryGuard: User is not allowed for tenant', tenantId, ncpdp);
      return false;
    }

    const [pharmacy, settings] = await Promise.all([
      await lastValueFrom(this.pharmaciesStore.get(ncpdp), { defaultValue: null }),
      await lastValueFrom(this.settingsStore.getSettings<InventorySettings>('inventory', `tenant:${tenantId}`), { defaultValue: null }),
    ]);
    const enrollment = pharmacy?.enrollments.find(enrl => enrl.tenantId === tenantId);

    if (!settings) {
      console.error('AuthInventoryGuard: Settings missing for tenant', tenantId);
      return false;
    }

    if (!enrollment) {
      console.error('AuthInventoryGuard: Enrollment missing for pharmacy and tenant', ncpdp, tenantId);
      return false;
    }

    return settings.visibility.programGroup[enrollment.enrollmentGroupId] ?? false;
  }

  // Portal routes name the tenant as a literal segment (consignment-inventory/INCY) instead of a :tenantId param;
  // the tenant-less parent page is available when any inventory tenant is
  private getTenantIds(routeUrl: string, params: Record<string, string>): string[] {
    if (params.tenantId) return [params.tenantId];
    const lastSegment = routeUrl.split('/').pop() ?? '';
    return INVENTORY_TENANTS.includes(lastSegment) ? [lastSegment] : INVENTORY_TENANTS;
  }
}
